import { Alert, Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { Icon } from '@/components/Icon/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import { getAPIErrorMessage } from '@/utils/api';

import { DocumentSelector } from '../components/DocumentSelector';

import { StyledAlertBackSide } from './Drawers.styled';

type DocumentUploadStepProps = {
  titleKey: string;
  guidanceKey?: string;
  guidanceParams?: Record<string, unknown>;
  descriptionKey?: string;
  showBackAlert?: boolean;
  showDownload?: boolean;
  isDownloading?: boolean;
  onDownload?: () => void;
  downloadError?: Error | null;
  onSetFile: (value: File | string) => void;
};

export function DocumentUploadStep({
  titleKey,
  guidanceKey,
  guidanceParams,
  descriptionKey,
  showBackAlert,
  showDownload,
  isDownloading,
  onDownload,
  downloadError,
  onSetFile,
}: Readonly<DocumentUploadStepProps>) {
  const { t, translateIfExists } = useTranslate();

  return (
    <>
      {showBackAlert && (
        <StyledAlertBackSide
          icon={<Icon name="check-circle" />}
          color="success"
          variant="standard"
        >
          {t('verification.documentUploadBox.frontUploadedPleaseAddBack')}
        </StyledAlertBackSide>
      )}
      <DocumentSelector
        title={t(titleKey)}
        description={
          <Stack direction="column" gap={1}>
            {guidanceKey && (
              <Typography
                variant="body2"
                color={theme => theme.palette.grey[800]}
                textAlign="center"
              >
                <Trans
                  i18nKey={guidanceKey}
                  values={guidanceParams}
                  components={{
                    b: <Typography component="span" variant="body2Bold" />,
                    i: <i />,
                    br: <br />,
                  }}
                />
              </Typography>
            )}

            {descriptionKey && (
              <Typography
                variant="body2"
                color={theme => theme.palette.grey[800]}
                textAlign="center"
              >
                {translateIfExists(descriptionKey, { defaultValue: '' })}
              </Typography>
            )}

            {showDownload && (
              <>
                <LoadingButton
                  isLoading={!!isDownloading}
                  onClick={onDownload}
                  startIcon={<Icon name="download" />}
                >
                  {t('requiredDocUpload.button.donwloadForm')}
                </LoadingButton>
                {downloadError && (
                  <Alert
                    icon={<Icon name="alert" />}
                    color="error"
                    variant="standard"
                  >
                    {getAPIErrorMessage(downloadError)}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        }
        onSetFile={onSetFile}
      />
    </>
  );
}
